import { isValidElement } from 'react';

const SEGMENT_SCORE = {
    STATIC: 10,
    DYNAMIC: 3,
    OPTIONAL: 2,
    SPLAT: 1,
};

/**
 * Returns a weight for a single path segment.
 *
 * @function getSegmentWeight
 *
 * @param {string} segment - A single path segment.
 *
 * @returns {number} Weight for the segment.
 * @memberof Mojito.Applications.Common
 */
export const getSegmentWeight = segment => {
    if (segment === '*') {
        return SEGMENT_SCORE.SPLAT;
    }
    if (!segment.startsWith(':')) {
        return SEGMENT_SCORE.STATIC;
    }
    if (segment.endsWith('?')) {
        return SEGMENT_SCORE.OPTIONAL;
    }
    if (segment.endsWith('*') || segment.endsWith('+')) {
        return SEGMENT_SCORE.SPLAT;
    }
    return SEGMENT_SCORE.DYNAMIC;
};

/**
 * Returns path segments for a route element.
 *
 * @function getPathSegments
 *
 * @param {object} route - A route element.
 *
 * @returns {Array<string>|null} Path segments, or null when the route has no path.
 * @memberof Mojito.Applications.Common
 */
export const getPathSegments = route => {
    const path = route?.props?.path;
    if (!isValidElement(route) || !path) {
        return null;
    }
    return path.split('/').filter(Boolean);
};

/**
 * Compares two routes by path weight segment-by-segment.
 * Static segments outrank dynamic and splat segments at the same position.
 * When one route is a prefix of the other with equal weights, the longer route ranks higher.
 *
 * @function compareRoutesByWeight
 *
 * @param {object} routeA - First route element.
 * @param {object} routeB - Second route element.
 *
 * @returns {number} Negative when routeA ranks higher, positive when routeB ranks higher.
 * @memberof Mojito.Applications.Common
 */
export const compareRoutesByWeight = (routeA, routeB) => {
    const segmentsA = getPathSegments(routeA);
    const segmentsB = getPathSegments(routeB);

    if (segmentsA === null && segmentsB === null) {
        return 0;
    }
    if (segmentsA === null) {
        return 1;
    }
    if (segmentsB === null) {
        return -1;
    }

    const maxLen = Math.max(segmentsA.length, segmentsB.length);
    for (let i = 0; i < maxLen; i++) {
        const weightA = i < segmentsA.length ? getSegmentWeight(segmentsA[i]) : 0;
        const weightB = i < segmentsB.length ? getSegmentWeight(segmentsB[i]) : 0;
        if (weightA !== weightB) {
            return weightB - weightA;
        }
    }

    return 0;
};

/**
 * Sorts route elements by path weight so static segments outrank dynamic and splat segments.
 * Routes with equal weight keep their original declaration order.
 *
 * @function sortRoutesByWeight
 *
 * @param {Array} routes - Route elements to sort.
 *
 * @returns {Array} Routes sorted by descending weight.
 * @memberof Mojito.Applications.Common
 */
export const sortRoutesByWeight = routes => {
    return routes
        .map((route, index) => ({ route, index }))
        .sort((a, b) => compareRoutesByWeight(a.route, b.route) || a.index - b.index)
        .map(({ route }) => route);
};
